import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

export default function InviteCoach() {
  const { apiFetch, activeTeam } = useAuth();
  const [form, setForm] = useState({ email: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaid, setIsPaid] = useState(true);

  const loadCoaches = useCallback(async () => {
    try {
      const data = await apiFetch('/api/admin/team-coaches');
      setCoaches(data.coaches);
      setInvitations(data.invitations);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadCoaches(); }, [loadCoaches]);
  useEffect(() => { apiFetch('/api/admin/is-paid').then(d => setIsPaid(d.isPaid)).catch(() => {}); }, [apiFetch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    try {
      const data = await apiFetch('/api/admin/coaches', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (data.linked) {
        setSuccess(`${data.coach.first_name} ${data.coach.last_name} has been added to your team.`);
      } else {
        setSuccess(`Invitation sent to ${form.email}. They'll receive an email to set up their account.`);
      }
      setForm({ email: '' });
      loadCoaches();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="admin-page">
      <h1 className="page-title">Coaches</h1>

      {/* Coaches List */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '0.95rem', marginBottom: 12 }}>Team Coaches</h3>
        {loading && <div className="loading"><div className="spinner" /></div>}
        {!loading && coaches.length === 0 && invitations.length === 0 && (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No coaches on this team yet. Invite one below.</div>
        )}
        {!loading && coaches.map(c => (
          <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--card-border)' }}>
            <div>
              <strong style={{ fontSize: '0.9rem' }}>{c.first_name} {c.last_name}</strong>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 8 }}>{c.email}</span>
            </div>
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, background: 'rgba(46,204,113,0.15)', color: '#2ecc71', fontWeight: 600 }}>Active</span>
          </div>
        ))}
        {!loading && invitations.map(inv => (
          <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--card-border)' }}>
            <div>
              <span style={{ fontSize: '0.9rem' }}>{inv.email}</span>
            </div>
            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, background: 'rgba(243,156,18,0.15)', color: '#f39c12', fontWeight: 600 }}>Pending</span>
          </div>
        ))}
      </div>

      {/* Invite Form */}
      <div className="card">
        <h3 style={{ fontSize: '0.95rem', marginBottom: 12 }}>Invite Coach</h3>
        {activeTeam?.is_demo ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '12px 0' }}>
            This is not available on demo accounts.
          </div>
        ) : !isPaid && (coaches.length + invitations.length) >= 2 ? (
          <div style={{ background: 'rgba(247,124,0,0.1)', border: '1px solid rgba(247,124,0,0.3)', borderRadius: 8, padding: '12px 16px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            <i className="fas fa-lock" style={{ marginRight: 8 }}></i>
            Free plans allow up to 2 coaches per team. <a href="/admin/upgrade?from=coaches" style={{ color: 'var(--orange)', fontWeight: 600 }}>Upgrade for unlimited →</a>
          </div>
        ) : (
          <>
            {success && <div style={{ color: 'var(--success)', marginBottom: 16, fontWeight: 600 }}>{success}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} autoCapitalize="none" required />
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                If this email is already registered, they'll be added to your team. Otherwise, they'll receive an invitation email to set up their account.
              </div>
              <button className="btn btn-orange" type="submit" disabled={saving} style={{ width: '100%' }}>
                {saving ? 'Inviting...' : 'Invite Coach'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
